import React, { useEffect, useState } from "react";
import {
  PageHeader,
  Button,
  Badge,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SpinnerLoader,
  Toastify,
} from "lift-ui";
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Megaphone,
  Users,
  Video,
  Lock,
} from "lucide-react";
import { useProcessContext } from "./process-context";
import { useAuthContext } from "@lift-admin/utils/provider";

const Trigger = () => {
  const { canManage } = useAuthContext();
  const hasManageAccess = canManage("taxonomy");

  const {
    campaignLogs,
    runCampaignProcess,
    adsetsLogs,
    runAdsetsProcess,
    adsLogs,
    runAdsProcess,
    campaignDialogOpen,
    setCampaignDialogOpen,
    adsetsDialogOpen,
    setAdsetsDialogOpen,
    adsDialogOpen,
    setAdsDialogOpen,
    loading,
    setLoading,
    isError,
    setIsError,
  } = useProcessContext();

  const items = [
    {
      icon: <Megaphone className="h-16 w-16 text-[#334238]" />,
      label: "Campaign Trigger",
      id: "campaigns",
    },
    {
      icon: <Users className="h-16 w-16 text-[#334238]" />,
      label: "Adsets Trigger",
      id: "adsets",
    },
    {
      icon: <Video className="h-16 w-16 text-[#334238]" />,
      label: "Ads Trigger",
      id: "ads",
    },
  ];

  const triggerFunctions: Record<string, () => Promise<void>> = {
    "Campaign Trigger": runCampaignProcess,
    "Adsets Trigger": runAdsetsProcess,
    "Ads Trigger": runAdsProcess,
  };

  const setDialogOpenFunctions: Record<
    string,
    React.Dispatch<React.SetStateAction<boolean>>
  > = {
    "Campaign Trigger": setCampaignDialogOpen,
    "Adsets Trigger": setAdsetsDialogOpen,
    "Ads Trigger": setAdsDialogOpen,
  };

  const logsMap: Record<string, string[]> = {
    "Campaign Trigger": campaignLogs,
    "Adsets Trigger": adsetsLogs,
    "Ads Trigger": adsLogs,
  };

  const loadingMap: Record<string, boolean> = {
    "Campaign Trigger": loading?.campaigns || false,
    "Adsets Trigger": loading?.adsets || false,
    "Ads Trigger": loading?.ads || false,
  };

  const errorMap: Record<string, boolean> = {
    "Campaign Trigger": isError?.campaigns || false,
    "Adsets Trigger": isError?.adsets || false,
    "Ads Trigger": isError?.ads || false,
  };

  const handleItemClick = (label: string) => {
    if (!hasManageAccess) {
      Toastify(
        "error",
        "Only managers can trigger imports. Contact your administrator for access."
      );
      return;
    }
    if (loading?.campaigns || loading?.adsets || loading?.ads) {
      Toastify(
        "warning",
        "An import is in progress. Please try after some time."
      );
      return;
    }
    triggerFunctions[label]?.();
  };

  const handleDialogClose = (label: string) => {
    setDialogOpenFunctions[label]?.(false);
  };

  const renderLogs = (logs: string[], isLoading: boolean, isError: boolean) => {
    if (!logs.length) {
      return (
        <div className="text-center py-8 text-gray-500">
          No logs available.
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {logs.map((msg, idx) => {
          const isLast = idx === logs.length - 1;
          return (
            <div
              key={idx}
              className="flex items-start gap-3 p-3 border-b border-gray-200 last:border-b-0"
            >
              <div className="flex-1 text-sm text-gray-700">{msg}</div>
              <div className="flex-shrink-0">
                {isLast ? (
                  isError ? (
                    <XCircle className="h-5 w-5 text-red-500" />
                  ) : isLoading ? (
                    <Loader2 className="h-5 w-5 text-primary animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  )
                ) : (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader
        title="Triggers"
        renderCustomTop={
          <div className="flex items-center gap-3">
            {!hasManageAccess && (
              <div className="flex items-center gap-2 px-3 py-2 border ">
                <Lock className="h-4 w-4" />
                <span className="text-sm">
                  Only managers can trigger imports.
                </span>
              </div>
            )}
            {hasManageAccess && (
              <div className="flex items-center gap-2 px-3 py-2 border border-amber-400 rounded-md bg-amber-50 text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm">
                  Don't close the admin panel, refresh or turn off your internet
                  while any import is still in progress.
                </span>
              </div>
            )}
          </div>
        }
      />

      <div className="flex-1 px-8 pt-4 pb-4 overflow-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item, index) => (
              <div
                key={index}
                className={`border border-gray-200 rounded-lg p-6 text-center transition-shadow ${hasManageAccess ? 'hover:shadow-md' : 'opacity-75'}`}
              >
                <Button
                  variant="ghost"
                  className={`flex flex-col items-center gap-4 w-full h-auto p-4 ${hasManageAccess ? 'hover:bg-gray-50' : 'cursor-not-allowed'}`}
                  onClick={() => handleItemClick(item.label)}
                  disabled={loadingMap[item.label] || !hasManageAccess}
                >
                  <div className="flex items-center justify-center relative">
                    {item.icon}
                    {!hasManageAccess && (
                      <Lock className="h-4 w-4 text-gray-400 absolute -top-1 -right-1" />
                    )}
                  </div>
                  <div className={`text-base font-semibold ${hasManageAccess ? 'text-gray-900' : 'text-gray-500'}`}>
                    {item.label}
                  </div>
                </Button>
                <Badge
                  variant="destructive"
                  className="mt-4"
                  style={{
                    display: loadingMap[item.label] ? "inline-flex" : "none",
                  }}
                >
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Running
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 w-full"
                  onClick={() => setDialogOpenFunctions[item.label]?.(true)}
                >
                  See Logs
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Campaign Logs Sheet */}
      <Sheet
        open={campaignDialogOpen}
        onOpenChange={(open) => !open && handleDialogClose("Campaign Trigger")}
      >
        <SheetContent className="sm:max-w-lg flex flex-col">
          <SheetHeader>
            <SheetTitle>Campaign Import Logs</SheetTitle>
          </SheetHeader>
          <div className="mt-6 flex-1 min-h-0 overflow-y-auto">
            {renderLogs(
              campaignLogs,
              loading.campaigns,
              isError.campaigns
            )}
          </div>
          <SheetFooter className="border-t pt-4 mt-4 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => handleDialogClose("Campaign Trigger")}
            >
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Adsets Logs Sheet */}
      <Sheet
        open={adsetsDialogOpen}
        onOpenChange={(open) => !open && handleDialogClose("Adsets Trigger")}
      >
        <SheetContent className="sm:max-w-lg flex flex-col">
          <SheetHeader>
            <SheetTitle>Adsets Import Logs</SheetTitle>
          </SheetHeader>
          <div className="mt-6 flex-1 min-h-0 overflow-y-auto">
            {renderLogs(adsetsLogs, loading.adsets, isError.adsets)}
          </div>
          <SheetFooter className="border-t pt-4 mt-4 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => handleDialogClose("Adsets Trigger")}
            >
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Ads Logs Sheet */}
      <Sheet
        open={adsDialogOpen}
        onOpenChange={(open) => !open && handleDialogClose("Ads Trigger")}
      >
        <SheetContent className="sm:max-w-lg flex flex-col">
          <SheetHeader>
            <SheetTitle>Ads Import Logs</SheetTitle>
          </SheetHeader>
          <div className="mt-6 flex-1 min-h-0 overflow-y-auto">
            {renderLogs(adsLogs, loading.ads, isError.ads)}
          </div>
          <SheetFooter className="border-t pt-4 mt-4 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => handleDialogClose("Ads Trigger")}
            >
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Trigger;
